import React, { useState, useEffect } from 'react';
import styled, { css, keyframes } from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCheck,
  faExclamationTriangle,
  faInfoCircle
} from '@fortawesome/free-solid-svg-icons';

const slideIn = keyframes`
  from { opacity: 0; transform: translateY(-10px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const slideOut = keyframes`
  from { opacity: 1; transform: translateY(0); }
  to   { opacity: 0; transform: translateY(-10px); }
`;

const pulseCritical = keyframes`
  0%, 100% { box-shadow: 0 12px 28px rgba(0, 0, 0, 0.34); }
  50% { box-shadow: 0 16px 34px rgba(0, 0, 0, 0.5); }
`;

const variants = {
  success: {
    bg: '#e9fbf1',
    icon: '#22c55e',
    title: '#065f46',
    message: '#0f766e',
    close: 'rgba(6, 95, 70, 0.75)',
    closeHover: '#065f46'
  },
  error: {
    bg: '#fdecec',
    icon: '#ef4444',
    title: '#7f1d1d',
    message: '#991b1b',
    close: 'rgba(127, 29, 29, 0.75)',
    closeHover: '#7f1d1d'
  },
  warning: {
    bg: '#fff4e5',
    icon: '#f97316',
    title: '#7c2d12',
    message: '#9a3412',
    close: 'rgba(124, 45, 18, 0.75)',
    closeHover: '#7c2d12'
  },
  info: {
    bg: '#eaf5ff',
    icon: '#3b82f6',
    title: '#1e3a8a',
    message: '#1d4ed8',
    close: 'rgba(30, 58, 138, 0.75)',
    closeHover: '#1e3a8a'
  },
  neutral: {
    bg: '#efefef',
    icon: '#737373',
    title: '#404040',
    message: '#525252',
    close: 'rgba(64, 64, 64, 0.75)',
    closeHover: '#404040'
  },
  critical: {
    bg:
      'repeating-linear-gradient(-45deg, rgba(255, 230, 146, 0.26) 0 14px, rgba(255, 230, 146, 0) 14px 28px), linear-gradient(110deg, #6f0b0b, #b81414 58%, #d62020)',
    icon: '#ef4444',
    title: '#ffffff',
    message: '#ffe6e6',
    close: 'rgba(255, 255, 255, 0.82)',
    closeHover: '#ffffff'
  }
};

const Wrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 0.9rem;

  position: fixed;
  z-index: 99999999;
  top: ${({ type }) => (
    type === 'critical'
      ? '0'
      : 'max(0.75rem, env(safe-area-inset-top, 0px))'
  )};
  left: ${({ type }) => (
    type === 'critical'
      ? '0'
      : 'max(0.75rem, env(safe-area-inset-left, 0px))'
  )};
  right: ${({ type }) => (
    type === 'critical'
      ? '0'
      : 'max(0.75rem, env(safe-area-inset-right, 0px))'
  )};

  background: ${({ type }) => (variants[type] || variants.info).bg};

  padding: ${({ type }) => (
    type === 'critical'
      ? 'max(0.9rem, env(safe-area-inset-top, 0px)) 1rem 0.95rem'
      : '0.95rem 1.1rem'
  )};
  border-radius: ${({ type }) => (type === 'critical' ? '0' : '10px')};
  border-bottom: ${({ type }) => (type === 'critical' ? '4px solid rgba(255, 226, 142, 0.95)' : 'none')};
  max-width: ${({ type }) => (type === 'critical' ? 'none' : '680px')};
  margin: 0 auto;

  font-family: 'Inter', 'Roboto', sans-serif;

  animation: ${({ type, $closing, $persistent }) => {
    if (type === 'critical') {
      return css`
        ${slideIn} 0.35s ease-out, ${pulseCritical} 1.4s ease-in-out infinite
      `;
    }

    return css`
      ${$persistent ? slideIn : ($closing ? slideOut : slideIn)} 0.5s ease forwards
    `;
  }};

  @media (max-width: 640px) {
    gap: 0.7rem;
    padding: ${({ type }) => (
    type === 'critical'
      ? 'max(0.75rem, env(safe-area-inset-top, 0px)) 0.78rem 0.82rem'
      : '0.8rem 0.82rem'
  )};
    border-radius: ${({ type }) => (type === 'critical' ? '0' : '9px')};
  }
`;

const Icon = styled.div`
  width: ${({ type }) => (type === 'critical' ? '40px' : '36px')};
  height: ${({ type }) => (type === 'critical' ? '40px' : '36px')};
  min-width: ${({ type }) => (type === 'critical' ? '40px' : '36px')};

  border-radius: 50%;

  display: flex;
  align-items: center;
  justify-content: center;

  background: ${({ type }) => (variants[type] || variants.info).icon};
  color: #ffffff;

  font-size: 1rem;
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 0.15rem;
  flex: 1;
`;

const Title = styled.div`
  font-weight: 600;
  font-size: 0.95rem;
  color: ${({ type }) => (variants[type] || variants.info).title};
  line-height: 1.2;

  @media (max-width: 640px) {
    font-size: 0.88rem;
  }
`;

const Message = styled.div`
  font-size: 0.9rem;
  color: ${({ type }) => (variants[type] || variants.info).message};
  line-height: 1.3;
  word-break: break-word;

  @media (max-width: 640px) {
    font-size: 0.82rem;
  }
`;

const Close = styled.button`
  border: none;
  background: transparent;
  cursor: pointer;

  display: flex;
  align-items: center;
  justify-content: center;

  width: 40px;
  height: 40px;
  min-width: 40px;

  font-size: 1.4rem;
  line-height: 1;
  border-radius: 8px;

  color: ${({ type }) => (variants[type] || variants.info).close};
  touch-action: manipulation;

  &:hover {
    color: ${({ type }) => (variants[type] || variants.info).closeHover};
    background: rgba(255, 255, 255, 0.15);
  }
`;

const ICONS = {
  success: faCheck,
  warning: faExclamationTriangle,
  error: faExclamationTriangle,
  critical: faExclamationTriangle,
  info: faInfoCircle
};

const Alert = ({
  type = 'info',
  title,
  message,
  duration = 4000,
  onClose,
  persistent = false
}) => {
  const [closing, setClosing] = useState(false);

  useEffect(() => {
    if (persistent || duration <= 0) {
      setClosing(false);
      return undefined;
    }

    const timeout = Math.max(0, duration - 300);
    const timer = setTimeout(() => {
      setClosing(true);
    }, timeout);

    return () => clearTimeout(timer);
  }, [duration, persistent]);

  return (
    <Wrapper type={type} $closing={closing} $persistent={persistent}>
      <Icon type={type}>
        <FontAwesomeIcon icon={ICONS[type] || faInfoCircle} />
      </Icon>

      <Content>
        {title && <Title type={type}>{title}</Title>}
        {message && <Message type={type}>{message}</Message>}
      </Content>

      {onClose && <Close type={type} onClick={onClose}>×</Close>}
    </Wrapper>
  );
};

export default Alert;
